import React from 'react';
import type { PanelSize } from '../ToolExecution/types';
import { PanelControls } from '../ToolExecution/PanelControls';
import { PDFButton } from '../MessageContent/markdown/PDFButton';
import { ScreenshotButton } from '../MessageContent/markdown/ScreenshotButton';
import type { PrintMode } from '../../../../utils/pdfUtils';

import { useTranslation } from 'react-i18next';

interface FocusedViewHeaderProps {
  panelSize: PanelSize;
  onPanelSizeChange: (size: PanelSize) => void;
  onClose: () => void;
  title: string;
  contentRef: React.RefObject<HTMLDivElement | null>;
  pdfFilename: string;
  printMode: PrintMode;
}

export const FocusedViewHeader: React.FC<FocusedViewHeaderProps> = ({
  panelSize,
  onPanelSizeChange,
  onClose,
  title,
  contentRef,
  pdfFilename,
  printMode,
}) => {
  const { t } = useTranslation('pleri/chat');
  return (
    <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-[#FF732E]/10 p-1.5">
            <svg
              className="h-4 w-4 text-[#FF732E]"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
              />
            </svg>
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('focusedView')}
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <ScreenshotButton
          targetRef={contentRef}
          filename={pdfFilename.replace(/\.pdf$/, '.png')}
          title={t('saveAsImage')}
        />
        <PDFButton
          targetRef={contentRef}
          filename={pdfFilename}
          title={t('downloadAsPdf')}
          printMode={printMode}
        />
        <PanelControls
          panelSize={panelSize}
          onPanelSizeChange={onPanelSizeChange}
          onClose={onClose}
        />
      </div>
    </div>
  );
};
